/**
 * Authentication Context - HYB MASTER SHOP
 * Manages user authentication state and JWT tokens
 */

import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';

import { loginAdmin, AdminUserProfile } from '../api/auth';

type User = AdminUserProfile;

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string | null;
  idleTimeoutMinutes: number;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string; requirePasswordChange?: boolean; username?: string }>;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [idleTimeoutMinutes, setIdleTimeoutMinutes] = useState<number>(15);

  const idleTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const storedToken = localStorage.getItem('hyb_dashboard_token');
    const storedUser = localStorage.getItem('hyb_dashboard_user');

    // Persistent login: ignore token expiration and idle timeout
    setIdleTimeoutMinutes(0); // Disable idle timeout

    if (storedToken && storedUser) {
      try {
        const parsedUser: User = JSON.parse(storedUser);
        setUser(parsedUser);
        setToken(storedToken);
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('hyb_dashboard_token');
        localStorage.removeItem('hyb_dashboard_user');
        setToken(null);
      }
    }

    setIsLoading(false);
  }, []);

  const login = useCallback(async (username: string, password: string): Promise<{ success: boolean; error?: string; requirePasswordChange?: boolean; username?: string }> => {
    setIsLoading(true);

    try {
      const response = await loginAdmin({ username, password });

      // Check if user must change password on first login
      if (response.user.requirePasswordChange) {
        // Do NOT store credentials - user must change password first
        setIsLoading(false);
        return {
          success: true,
          requirePasswordChange: true,
          username: response.user.username
        };
      }

      // Store credentials for persistent login (no expiration)
      localStorage.setItem('hyb_dashboard_token', response.token);
      localStorage.setItem('hyb_dashboard_user', JSON.stringify(response.user));

      setUser(response.user);
      setToken(response.token);
      setIdleTimeoutMinutes(0); // Disable idle timeout

      return { success: true };
    } catch (error: any) {
      console.error('Login error:', error);
      // Handle error response properly
      // Backend sends: { success: false, error: { code: string, message: string } }
      const errorData = error?.response?.data?.error;
      const message = typeof errorData === 'string'
        ? errorData
        : errorData?.message || 'ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง';
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    if (idleTimerRef.current) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    // Clear all authentication data
    localStorage.removeItem('hyb_dashboard_token');
    localStorage.removeItem('hyb_dashboard_user');
    setUser(null);
    setToken(null);
  }, []);

  const scheduleIdleLogout = useCallback(() => {
    if (!user) return;
    if (idleTimeoutMinutes <= 0) return;
    if (idleTimerRef.current) {
      window.clearTimeout(idleTimerRef.current);
    }

    idleTimerRef.current = window.setTimeout(() => {
      toast.error('ออกจากระบบอัตโนมัติเพราะไม่มีการใช้งาน');
      logout();
    }, idleTimeoutMinutes * 60 * 1000);
  }, [user, idleTimeoutMinutes, logout]);

  useEffect(() => {
    if (!user) {
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      return;
    }

    const activityEvents: Array<keyof WindowEventMap> = ['mousemove', 'keydown', 'click', 'touchstart'];
    const handleActivity = () => scheduleIdleLogout();

    activityEvents.forEach(event => window.addEventListener(event, handleActivity));
    scheduleIdleLogout();

    return () => {
      activityEvents.forEach(event => window.removeEventListener(event, handleActivity));
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };
  }, [user, scheduleIdleLogout]);

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    // Support wildcard '*' for super admin
    return user.permissions.includes('*') || user.permissions.includes(permission) || user.permissions.includes('admin');
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    token,
    idleTimeoutMinutes,
    login,
    logout,
    hasPermission
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
